import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, FileWarning, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const isTradeLicence = (t = "") => /trade\s*licen/i.test(t);

const fmt = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return d;
  }
};

export default function TradeLicenseReminder({ profileTo = "/settings", scrollToId = "", className = "" }) {
  const [state, setState] = useState(null); // { kind: 'missing'|'expired'|'expiring', date, days }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/profile/documents");
        if (!alive) return;
        const docs = (data?.documents || []).filter((d) => isTradeLicence(d.doc_type));
        if (docs.length === 0) {
          setState({ kind: "missing" });
          return;
        }
        // pick the one expiring latest (most favourable) to represent the licence
        const best = docs.sort((a, b) => (b.days_left ?? -99999) - (a.days_left ?? -99999))[0];
        if (best.status === "expired") {
          setState({ kind: "expired", date: best.expiry_date });
        } else if (best.status === "expiring") {
          setState({ kind: "expiring", date: best.expiry_date, days: best.days_left });
        } else {
          setState(null); // valid or unknown-but-present → no reminder
        }
      } catch {
        if (alive) setState(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!state) return null;

  const danger = state.kind !== "expiring";
  const Icon = state.kind === "missing" ? FileWarning : state.kind === "expired" ? AlertTriangle : Clock;
  const title =
    state.kind === "missing"
      ? "Trade Licence not uploaded"
      : state.kind === "expired"
      ? "Your Trade Licence has expired"
      : "Your Trade Licence is expiring soon";
  const body =
    state.kind === "missing"
      ? "Upload your Trade Licence so your business stays FTA-compliant and verified for e-invoicing."
      : state.kind === "expired"
      ? `It expired on ${fmt(state.date)}. Renew it with your authority and re-upload the new licence to stay compliant.`
      : `It expires on ${fmt(state.date)}${state.days != null ? ` — in ${state.days} day${state.days === 1 ? "" : "s"}` : ""}. Upload the renewed licence to avoid any interruption.`;

  const tone = danger
    ? "border-rose-300 bg-rose-50 dark:border-rose-900/60 dark:bg-rose-950/40"
    : "border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/40";
  const iconTone = danger ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400";
  const btnTone = danger
    ? "bg-rose-600 hover:bg-rose-700 text-white"
    : "bg-amber-500 hover:bg-amber-600 text-white";

  const ctaLabel = state.kind === "missing" ? "Upload now" : "Update licence";
  const scrollTo = () => {
    const el = document.getElementById(scrollToId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      data-testid={`trade-license-reminder-${state.kind}`}
      className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border px-4 py-3 ${tone} ${className}`}
    >
      <Icon className={`w-5 h-5 shrink-0 ${iconTone}`} />
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</p>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{body}</p>
      </div>
      {scrollToId ? (
        <button
          type="button"
          onClick={scrollTo}
          data-testid="trade-license-reminder-cta"
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${btnTone}`}
        >
          {ctaLabel}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      ) : (
        <Link
          to={profileTo}
          data-testid="trade-license-reminder-cta"
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${btnTone}`}
        >
          {ctaLabel}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}
